import React, { useState, useEffect } from 'react';
import '../App.css';
import { openTelegramLink } from "../utils/telegramSdk";
import {useTelegram} from "../hooks/useTelegram";

const ASSET_PATH = '/images';
const API_URL = window.location.origin;

export function FaqPage({ onPageLoaded }) {
    const [expandedItems, setExpandedItems] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);
    const { isTelegram } = useTelegram();

    // Загрузка всех FAQ при монтировании
    useEffect(() => {
        console.log('🔄 FaqPage: useEffect triggered');

        // Устанавливаем таймаут на случай, если загрузка зависла
        const timeoutId = setTimeout(() => {
            console.log('⚠️ FaqPage: Таймаут загрузки, принудительно завершаем');
            setLoading(false);
            if (onPageLoaded) {
                onPageLoaded();
            }
        }, 5000);

        fetchAllFaq().then(() => {
            console.log('✅ FaqPage: Все данные загружены');
            setLoading(false);
            clearTimeout(timeoutId);
            if (onPageLoaded) {
                console.log('📢 FaqPage: Вызываем onPageLoaded');
                onPageLoaded();
            }
        }).catch((error) => {
            console.error('❌ FaqPage: Ошибка загрузки данных:', error);
            setLoading(false);
            clearTimeout(timeoutId);
            if (onPageLoaded) {
                onPageLoaded();
            }
        });

        return () => clearTimeout(timeoutId);
    }, []);

    // Поиск при изменении запроса
    useEffect(() => {
        if (searchQuery.trim().length >= 2) {
            performSearch();
        } else if (searchQuery.trim().length === 0) {
            setSearchResults([]);
            setHasSearched(false);
        }
    }, [searchQuery]);

    const fetchAllFaq = async () => {
        try {
            console.log('📡 FaqPage: Запрос всех FAQ...');
            const response = await fetch(`${API_URL}/api/faq`);
            const data = await response.json();

            if (data.success) {
                setCategories(data.categories);
                console.log('📚 FaqPage: Категорий получено:', data.categories?.length || 0);
            } else {
                setError('Ошибка загрузки FAQ');
                throw new Error('Failed to load FAQ');
            }
        } catch (error) {
            console.error('❌ Ошибка загрузки FAQ:', error);
            setError('Не удалось подключиться к серверу');
            throw error;
        }
    };

    const handleSupport = async () => {
        if (isTelegram) {
            openTelegramLink('https://t.me/HYDSupport_bot');
        } else {
            window.location.href = 'https://t.me/HYDSupport_bot';
            //window.open('https://t.me/HydraSuppVP', '_blank');
        }

    };

    const performSearch = async () => {
        if (searchQuery.trim().length < 2) return;

        setSearchLoading(true);
        setHasSearched(true);

        try {
            console.log('🔍 FaqPage: Поиск по запросу:', searchQuery);
            const response = await fetch(`${API_URL}/api/faq/search?q=${encodeURIComponent(searchQuery)}`);
            const data = await response.json();

            if (data.success) {
                setSearchResults(data.results);
                console.log('🔍 FaqPage: Найдено результатов:', data.results?.length || 0);
            }
        } catch (error) {
            console.error('❌ Ошибка поиска:', error);
        } finally {
            setSearchLoading(false);
        }
    };

    const toggleItem = (id) => {
        setExpandedItems(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const clearSearch = () => {
        setSearchQuery('');
        setSearchResults([]);
        setHasSearched(false);
    };

// Функция для рендеринга HTML-контента с поддержкой двойных переносов
    const renderHtmlContent = (htmlString) => {
        if (!htmlString) return null;

        let formattedHtml = htmlString;

        // Сначала заменяем экранированные последовательности
        formattedHtml = formattedHtml
            .replace(/\\n\\n/g, '\n\n')  // Экранированные \n\n
            .replace(/\\n/g, '\n');       // Экранированные \n

        // Заменяем \n\n на <br><br>, затем одиночные \n на <br>
        formattedHtml = formattedHtml
            .replace(/\n\n/g, '<br><br>')
            .replace(/\n/g, '<br>');

        // Дополнительно обрабатываем возможные двойные пробелы и табуляции
        formattedHtml = formattedHtml
            .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')  // Табуляции в пробелы
            .replace(/  /g, ' &nbsp;');                   // Двойные пробелы

        // Если в тексте есть HTML-теги, оставляем их как есть
        // Если нет, оборачиваем в параграфы для лучшего отображения
        const hasHtmlTags = /<[^>]+>/.test(formattedHtml);

        if (!hasHtmlTags && formattedHtml.includes('<br>')) {
            // Разбиваем на параграфы по двойным переносам
            const paragraphs = formattedHtml.split('<br><br>');
            if (paragraphs.length > 1) {
                formattedHtml = paragraphs
                    .map(p => `<p>${p.replace(/<br>/g, '<br>')}</p>`)
                    .join('');
            }
        }

        return (
            <div
                className="html-content"
                dangerouslySetInnerHTML={{ __html: formattedHtml }}
            />
        );
    };

    console.log('🎨 FaqPage: Рендер, loading =', loading);

    // Если загрузка
    if (loading) {
        console.log('⏳ FaqPage: Показываем null (загрузка)');
        return null;
    }

    // Если ошибка
    if (error) {
        console.log('❌ FaqPage: Ошибка, показываем сообщение');
        return (
            <div className="faq-page page-enter-animate">
                <div className="faq-header">
                    <div className="faq-title-section">
                        <h1 className="faq-title">FAQ</h1>
                        <p className="faq-subtitle">Часто задаваемые вопросы</p>
                    </div>
                </div>
                <div className="error-message" style={{ textAlign: 'center', padding: '40px' }}>
                    {error}
                </div>
            </div>
        );
    }

    // Если поиск не дал результатов
    if (hasSearched && searchResults.length === 0 && !searchLoading) {
        console.log('📭 FaqPage: Поиск не дал результатов');
        return (
            <div className="faq-page page-enter-animate">
                <div className="faq-header">
                    <div className="faq-title-section">
                        <h1 className="faq-title">FAQ</h1>
                        <p className="faq-subtitle">Часто задаваемые вопросы</p>
                    </div>

                    <div className="faq-search">
                        <div className="search-icon">
                            <img src={`${ASSET_PATH}/I0_305_637_3195.svg`} alt="Search" />
                        </div>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Поиск по вопросам..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button className="search-clear-btn" onClick={clearSearch}>
                                <img src={`${ASSET_PATH}/0_408.svg`} alt="Clear" />
                            </button>
                        )}
                    </div>
                </div>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
                    <p className="error-message-faq" style={{textAlign: 'center', padding: '20px', margin: '20px'}}>
                        К сожалению, мы не нашли подходящих ответов. Попробуйте уточнить запрос
                        или напишите в поддержку.
                    </p>
                </div>

                <div className="support-card">
                    <div className="support-card-content">
                        <h3 className="support-card-title">Не нашли ответ на свой вопрос?</h3>
                        <p className="support-card-description">
                            Обращайтесь в поддержку — постарайтесь подробно описать проблему,
                            чтобы мы смогли помочь максимально быстро.
                        </p>
                    </div>
                    <button className="support-button" onClick={handleSupport}>
                        <span>Обратиться в поддержку</span>
                    </button>
                </div>
            </div>
        );
    }

    // Если есть результаты поиска
    if (hasSearched && searchResults.length > 0 && !searchLoading) {
        console.log('✅ FaqPage: Показываем результаты поиска');
        return (
            <div className="faq-page page-enter-animate">
                <div className="faq-header">
                    <div className="faq-title-section">
                        <h1 className="faq-title">FAQ</h1>
                        <p className="faq-subtitle">Часто задаваемые вопросы</p>
                    </div>

                    <div className="faq-search">
                        <div className="search-icon">
                            <img src={`${ASSET_PATH}/I0_305_637_3195.svg`} alt="Search" />
                        </div>
                        <input
                            type="text"
                            className="search-input"
                            placeholder="Поиск по вопросам..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                            <button className="search-clear-btn" onClick={clearSearch}>
                                <img src={`${ASSET_PATH}/0_408.svg`} alt="Clear" />
                            </button>
                        )}
                    </div>

                    <div className="search-result-count">
                        Найдено по запросу: {searchResults.length}
                    </div>
                </div>

                <div className="faq-list">
                    {searchResults.map((result, index) => (
                        <div key={index} className="faq-item">
                            <div
                                className="faq-item-header"
                                onClick={() => toggleItem(`search_${result.subsectionId}`)}
                            >
                                <span className="faq-item-title">{result.subsectionTitle}</span>
                                <img
                                    src={`${ASSET_PATH}/${expandedItems[`search_${result.subsectionId}`] ? '0_97.svg' : '0_97.svg'}`}
                                    className={`faq-item-icon ${expandedItems[`search_${result.subsectionId}`] ? 'expanded' : ''}`}
                                    alt={expandedItems[`search_${result.subsectionId}`] ? 'Collapse' : 'Expand'}
                                />
                            </div>

                            {expandedItems[`search_${result.subsectionId}`] && (
                                <div className="legal-content" style={{ paddingTop: '8px' }}>
                                    <div className="legal-subitem expanded" style={{ padding: '12px' }}>
                                        <div className="privacy-text">
                                            {/* Используем dangerouslySetInnerHTML для рендеринга HTML */}
                                            {renderHtmlContent(result.subsectionContent)}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="faq-divider"></div>
                        </div>
                    ))}
                </div>
                <div className="support-card">
                    <div className="support-card-content">
                        <h3 className="support-card-title">Не нашли ответ на свой вопрос?</h3>
                        <p className="support-card-description">
                            Обращайтесь в поддержку — постарайтесь подробно описать проблему,
                            чтобы мы смогли помочь максимально быстро.
                        </p>
                    </div>
                    <button className="support-button" onClick={handleSupport}>
                        <span>Обратиться в поддержку</span>
                    </button>
                </div>
            </div>
        );
    }

    // Обычный вид (все категории из БД)
    console.log('✅ FaqPage: Показываем все категории');
    return (
        <div className="faq-page page-enter-animate">
            <div className="faq-scroll-container">
            <div className="faq-header">
                <div className="faq-title-section">
                    <h1 className="faq-title">FAQ</h1>
                    <p className="faq-subtitle">Часто задаваемые вопросы</p>
                </div>

                <div className="faq-search">
                    <div className="search-icon">
                        <img src={`${ASSET_PATH}/I0_305_637_3195.svg`} alt="Search" />
                    </div>
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Поиск по вопросам..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                        <button className="search-clear-btn" onClick={clearSearch}>
                            <img src={`${ASSET_PATH}/0_408.svg`} alt="Clear" />
                        </button>
                    )}
                </div>
            </div>

            <div className="faq-list">
                {categories.map(category => (
                    <div key={category.id} className="faq-item">
                        <div
                            className="faq-item-header"
                            onClick={() => toggleItem(`cat_${category.id}`)}
                        >
                            <span className="faq-item-title">{category.title}</span>
                            <img
                                src={`${ASSET_PATH}/${expandedItems[`cat_${category.id}`] ? '0_97.svg' : '0_97.svg'}`}
                                className={`faq-item-icon ${expandedItems[`cat_${category.id}`] ? 'expanded' : ''}`}
                                alt={expandedItems[`cat_${category.id}`] ? 'Collapse' : 'Expand'}
                            />
                        </div>

                        {expandedItems[`cat_${category.id}`] && (
                            <div className="legal-content">
                                {category.subsections.map(subsection => (
                                    <div key={subsection.id} className="legal-subitem expanded" style={{ marginBottom: '8px' }}>
                                        <div
                                            className="legal-subitem-header"
                                            onClick={() => toggleItem(`sub_${subsection.id}`)}
                                            style={{ padding: '8px 0' }}
                                        >
                                            <span className="legal-subitem-title">{subsection.title}</span>
                                            <img
                                                src={`${ASSET_PATH}/${expandedItems[`sub_${subsection.id}`] ? '0_97.svg' : '0_97.svg'}`}
                                                className={`legal-subitem-icon ${expandedItems[`sub_${subsection.id}`] ? 'expanded' : ''}`}
                                                alt={expandedItems[`sub_${subsection.id}`] ? 'Collapse' : 'Expand'}
                                            />
                                        </div>

                                        {expandedItems[`sub_${subsection.id}`] && (
                                            <div className="privacy-text" style={{ marginTop: '8px' }}>
                                                <div className="privacy-divider"></div>
                                                {/* Используем dangerouslySetInnerHTML для рендеринга HTML */}
                                                <div className="privacy-paragraph">
                                                    {renderHtmlContent(subsection.content)}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                        {!expandedItems[`cat_${category.id}`] && (
                            <div className="faq-divider"></div>
                        )}
                    </div>
                ))}
            </div>
            <div className="support-card">
                <div className="support-card-content">
                    <h3 className="support-card-title">Не нашли ответ на свой вопрос?</h3>
                    <p className="support-card-description">
                        Обращайтесь в поддержку — постарайтесь подробно описать проблему,
                        чтобы мы смогли помочь максимально быстро.
                    </p>
                </div>
                <button className="support-button" onClick={handleSupport}>
                    <span>Обратиться в поддержку</span>
                </button>
            </div>
        </div>
        </div>
    );
}
